import { Rank } from '../../sim/defs';
import { Fam } from '../../sim/pools/BulletPool';
import { defineEnemy, Motion, Pattern } from '../schema';

/** E-ORG2 — COUVEUR. La priorité de cible, mais MOBILE et GARDÉE.
 *
 *  La Pondeuse s'ancre en haut, immobile : une cible prioritaire qu'on va
 *  chercher quand on veut, dont les œufs tombent droit et éclosent vite en Larves
 *  qui piquent puis s'en vont. Le Couveur pose la même question — couper la
 *  source — sous deux contraintes neuves : il DÉRIVE (impossible de le poster
 *  dans son viseur et de l'oublier), et il s'entoure d'un anneau lent (on ne
 *  l'approche pas gratuitement). Surtout, ce qu'il sème ne PART pas : ses spores
 *  s'installent bas et STAGNENT, épaississant peu à peu le mi-terrain d'un nuage
 *  qu'il faut désormais traverser.
 *
 *  ── Ce qu'il enseigne ──
 *  Que couper la source coûte ici un déplacement ET une prise de risque :
 *  rejoindre une cible qui fuit en perçant son anneau, avant que le terrain ne se
 *  referme. Laissé tranquille, le Couveur ne lâche pas une bordée de frappeurs
 *  (la facture de la Pondeuse) mais un ENCOMBREMENT durable — il ne tue pas, il
 *  prend de la place, et la place perdue est autant de terrain d'esquive en moins
 *  pour tout ce qui suit dans la vague. La bonne réponse n'est pas d'esquiver
 *  mieux, c'est de trancher tôt.
 *
 *  ── Pourquoi un anneau et non un tir visé ──
 *  Le tir visé de la Pondeuse interdit seulement de se garer pile sous elle. Le
 *  Couveur doit, lui, se DÉFENDRE pendant qu'on vient le chercher : l'anneau lent
 *  fait de l'approche un passage à négocier, sans jamais poursuivre le joueur —
 *  c'est une pression de place, cohérente avec la place que ses spores volent.
 *
 *  ── Bornée, sans exception ──
 *  Quatre spores au maximum sur toute sa vie (`brood.max`). Un couveur non borné
 *  saturerait le pool (256 slots) et le budget de frame. La chaîne termine sur la
 *  Spore, qui ne couve rien. */
export default defineEnemy({
  id: 'couveur',
  rank: Rank.Heavy,
  hp: 220,
  visualRadius: 34,
  score: 340,
  telegraph: 1.1,
  drops: 3,
  // Dérive lente et large : assez pour qu'on ne mémorise pas où le viser, assez
  // peu pour qu'on puisse aller le chercher entre deux volées.
  motion: { kind: Motion.Hover, speed: 240, amplitude: 100, frequency: 0.35, holdY: 340 },
  brood: {
    // Il sème des SPORES — feuille de la chaîne, elles ne couvent rien.
    enemy: 'spore',
    every: 3.2,
    // ⚠️ La borne dure : quatre spores, jamais plus, quelle que soit sa longévité.
    max: 4,
    // Sous lui : la spore naît juste dessous et se laisse couler vers le mi-bas.
    offY: -44,
  },
  emitter: {
    // Anneau lent : pression d'approche, jamais de poursuite. Il tourne d'une
    // volée à l'autre (`stepDeg`) pour qu'aucune place ne reste sûre à demeure.
    pattern: Pattern.Ring,
    every: 2.4,
    count: 8,
    speed: 300,
    stepDeg: 11,
    firstDelay: 1.6,
    family: Fam.EnemyPurple,
  },
});
